import { createApiClient } from '../api/client';
import type { AdvisorLinkedOfferResponse } from '../api/types';

/**
 * The query definition for `GET /advisor/offers/{advisorId}` — which firm and which person
 * ONE `?advisor=<advisorId>` connect link names (steward-app#1364).
 *
 * ⚠️⚠️ **A SEPARATE KEY FROM `['advisor-firms']`, ON PURPOSE.** That entry has many
 * observers — every component that calls `useAdvisorFirm`, plus this page, which spreads
 * `advisorFirmsQueryOptions` directly — and they all go through that ONE factory because its
 * own header (steward-app#671) records what happens when one key serves two shapes:
 * `TypeError: filter is not a function` on this very page. Folding a per-link variant
 * into it would either re-key the every-page query for every session or reintroduce exactly
 * that. This is one small, single-purpose entry with one observer.
 *
 * ⚠️ **Fires ONLY when the URL carries a deep link.** `enabled` is false without an id, so an
 * ordinary visit to People → Advisors spends no request here at all. A family who followed
 * an advisor's link spends exactly one, and it is a directory read the façade answers off a
 * five-minute in-process cache with no appcore call (`resolveOffer`'s own docblock).
 *
 * ⚠️ **`{ offer: null }` is the ordinary answer and it is a 200.** The route deliberately does
 * not 404 an unknown, unlisted or ambiguous id, because the deep link's whole specification is
 * that it degrades silently — see `Advisors.tsx`'s effect. So a `null` here is data, never an
 * error, and the caller must not render one.
 *
 * ⚠️⚠️ **Workspace-LESS, like `advisor-firms` and unlike `advisor-connections`, and that is
 * why {@link LINKED_ADVISOR_OFFER_QUERY_KEY} is on `WorkspaceProvider`'s reset keep-list.** The
 * answer is a fact about the published directory and cannot vary by which family is active, so
 * it takes no workspace in its key — the same property that put `advisor-firms`,
 * `advisor-clients` and `advisor-summary` on that list.
 *
 * ⚠️ **An earlier version of this paragraph said the key was NOT on the keep-list and that
 * this was harmless because a reset "would leave the link unconsumed and the page ordinary".
 * That was wrong twice, and this branch's code review caught it.** `resetQueries` calls
 * `query.reset()` and then `refetchQueries({ type: 'active' })` (@tanstack/query-core,
 * `queryClient.js`), so an active observer is REFETCHED rather than abandoned; and the reset
 * that reaches this query is not a user's workspace switch at all but
 * `WorkspaceProvider`'s initial-workspace correction, which fires on precisely the COLD LOAD
 * the deep link is followed from. The measured cost was two identical GETs on the one path this
 * route exists for. `Advisors.test.tsx` counts them.
 *
 * Deliberately NOT coerced or validated here — the payload is coerced by `linkedAdvisorOffer`
 * (`src/pages/people/advisorOffers.ts`), which is pure and asserted without a DOM. Contrast
 * `advisorConnectionsQueryOptions`, which throws on an unreadable body: "no advisory firm is
 * connected" is a claim about who can see a family's finances and must fail loudly, whereas an
 * unreadable answer here means one link does not open, which is what an unknown id already
 * does.
 */
/** The React Query key prefix for `GET /advisor/offers/{advisorId}`. Exported so
 *  `WorkspaceProvider`'s reset keep-list reads it from here rather than retyping the string —
 *  the convention that list already follows for the three advisor keys beside it, and for the
 *  reason its own comment gives: a silent typo would start resetting one on every switch with
 *  nothing failing to say so. */
export const LINKED_ADVISOR_OFFER_QUERY_KEY = ['advisor-linked-offer'] as const;

export function linkedAdvisorOfferQueryOptions(token: string | null, advisorId: string | null) {
  return {
    // The id is the SECOND element because it is the whole of the question, and the first stays
    // the bare prefix above: `WorkspaceProvider`'s keep-list matches on `queryKey[0]`.
    // Opaque and bounded; it is put in a URL path segment (encoded below) and compared, never
    // parsed.
    queryKey: [...LINKED_ADVISOR_OFFER_QUERY_KEY, advisorId] as const,
    queryFn: () => createApiClient(token!)
      .get<AdvisorLinkedOfferResponse>(`/advisor/offers/${encodeURIComponent(advisorId!)}`),
    enabled: !!token && !!advisorId,
    staleTime: 5 * 60_000,
  };
}
